import { creatorbase } from '@round/api';
import { Button, Sheet, Skeleton, Tooltip, toast } from '@round/creatorbase-ui';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import { useAllPosts, useCampaignStats, useClient, usePoolsListInfinite } from '@round/creatorbase-helpers/api-hooks';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { platformLabels } from 'Modules/Plans/helpers';
import { isNumber } from 'utility/utility';
import CampaignHeader from '../AddCreatorsToCampaignModal/Header/CampaignHeader';
import { getProjectDetailsPostsParams } from '../../hooks/posts/getProjectDetailsPostsParams';
import { getCreatorAccountId } from './helpers';
import { getDefaultPoolSelectParams, mapPoolToItem, PoolItem } from './usePoolCombobox';
import PoolCreatorsTable from './PoolCreatorsTable/PoolCreatorsTable';
import { Meta } from './PoolCreatorsTable/helpers';
import PoolSelect from './PoolSelect/PoolSelect';
import { useAddCreatorsFromPool } from './useAddCreatorsFromPool';
import { usePoolCreators } from './usePoolCreators';
import { usePoolCreatorSelection } from './usePoolCreatorSelection';
import styles from './AddFromPoolModal.module.css';

type Props = {
    isOpen: boolean;
    onClose: (hasUpdated: boolean) => void;
    campaign: creatorbase.TiktokCampaign | creatorbase.InstagramCampaign | undefined;
};

const AddFromPoolModal = ({ isOpen, onClose, campaign }: Props) => {
    const [selectedPool, setSelectedPool] = useState<PoolItem | null>(null);
    const { selectedClientId } = useClientSelect();
    const isClientSelected = isNumber(selectedClientId);

    // Kept apart from the combobox's own query so that typing a search can't move the default.
    const {
        data: [defaultPool],
        isLoading: isDefaultPoolLoading,
        hasError: hasPoolsError,
    } = usePoolsListInfinite(getDefaultPoolSelectParams(selectedClientId), {
        enabled: isOpen && isClientSelected,
    });

    // base-ui compares the combobox value by reference, so this has to stay stable across renders.
    const pool = useMemo(
        () => selectedPool ?? (defaultPool ? mapPoolToItem(defaultPool) : null),
        [selectedPool, defaultPool]
    );

    const {
        creators,
        count,
        hiddenCount,
        isHiddenCountLoading,
        isLoading,
        hasError,
        getCreatorImages,
        getAreCreatorImagesLoading,
    } = usePoolCreators(
        { poolId: pool?.value, platform: campaign?.platform, clientId: selectedClientId },
        { enabled: isOpen && !!pool }
    );

    // Creators added this session drop out of the list rather than showing as addable again.
    const [addedAccountIds, setAddedAccountIds] = useState<Set<number>>(() => new Set());

    // Unlike addedAccountIds this survives a pool change, so closing still tells the caller to refresh.
    const [hasUpdated, setHasUpdated] = useState(false);

    const listedCreators = creators.filter((creator) => {
        const accountId = getCreatorAccountId(creator, campaign?.platform);
        return !accountId || !addedAccountIds.has(accountId);
    });

    const listedCount = typeof count === 'number' ? count - addedAccountIds.size : undefined;

    const { selectedAccountIds, toggleCreator, selectCreators, deselectCreators, clearSelection } =
        usePoolCreatorSelection();

    const { data: campaignPosts, isLoading: areCampaignPostsLoading } = useAllPosts(
        getProjectDetailsPostsParams(campaign?.id),
        { enabled: isOpen && !!campaign?.id }
    );

    const { invalidate: invalidateCampaignStats } = useCampaignStats(
        campaign?.id ? { campaign_id: campaign.id } : null,
        {}
    );

    const { client } = useClient(selectedClientId);
    const { addCreators, isAdding } = useAddCreatorsFromPool({
        campaign,
        isPaymentEnabled: client?.is_payment_enabled,
    });

    const accountIdsInCampaign = new Set(campaignPosts.map((post) => post.client_influencer_account_id));

    const handlePoolSelect = (nextPool: PoolItem) => {
        setSelectedPool(nextPool);
        clearSelection();
        setAddedAccountIds(new Set());
    };

    // Both call sites key this modal on the campaign, so closing remounts it and resets every
    // piece of state below.
    const handleClose = () => {
        onClose(hasUpdated);
    };

    const handleAddCreators = async () => {
        if (!campaign) {
            return;
        }

        const creatorsToAdd = listedCreators.flatMap((creator) => {
            const accountId = getCreatorAccountId(creator, campaign.platform);
            const account = creator.platforms?.[campaign.platform]?.[0];

            if (!accountId || !selectedAccountIds.has(accountId)) {
                return [];
            }

            return [
                {
                    clientInfluencerAccountId: accountId,
                    cost: account?.cost ?? null,
                    currencyId: account?.currency_id ?? null,
                },
            ];
        });

        const { addedAccountIds: added, failedAccountIds: failed } = await addCreators(creatorsToAdd);

        if (added.length) {
            setAddedAccountIds((previous) => new Set([...previous, ...added]));
            setHasUpdated(true);
            deselectCreators(added);
            invalidateCampaignStats(campaign.id);
        }

        if (!failed.length) {
            toast.success(`${added.length} creator${added.length === 1 ? '' : 's'} added`);
            return;
        }

        if (!added.length) {
            toast.error(`Could not add ${failed.length} creator${failed.length === 1 ? '' : 's'}`);
            return;
        }

        toast.error(`Added ${added.length} of ${added.length + failed.length} creators. ${failed.length} failed`);
    };

    // Nothing is fetched until a pool resolves, and the table shouldn't flash its empty state meanwhile.
    // The client can still be resolving too, and the default pool query stays disabled until it lands.
    const isAwaitingPool = !pool && (isDefaultPoolLoading || !isClientSelected);
    const hasNoPools = !pool && !isAwaitingPool && !hasPoolsError;

    const meta: Meta = {
        isLoading: isLoading || isAwaitingPool,
        hasError,
        getCreatorImages,
        getAreCreatorImagesLoading,
        // Every row is a member on the campaign's platform; the sheet only opens with a campaign.
        getRowSelectedPlatform: () => campaign?.platform ?? 'tiktok',
        platform: campaign?.platform,
        selectedAccountIds,
        isAlreadyInCampaign: (accountId) => accountIdsInCampaign.has(accountId),
        areCampaignPostsLoading,
    };

    return (
        <Sheet
            open={isOpen}
            onOpenChange={(_, event) => {
                // The combobox list is portaled out of the sheet, so pressing it reads as an outside
                // press. Only a press that lands on the overlay is the user dismissing the sheet.
                if (event.reason === 'outside-press' && !isOverlayPress(event.event)) {
                    return;
                }
                handleClose();
            }}
        >
            <Sheet.Content side="bottom" className={styles.sheet}>
                <Sheet.Header className={cn(styles.section, styles.header)}>
                    <CampaignHeader campaign={campaign} onClose={handleClose} />
                </Sheet.Header>

                <div className={cn(styles.section, styles.toolbar)}>
                    <PoolSelect
                        clientId={selectedClientId}
                        isEnabled={isOpen}
                        selectedPool={pool}
                        isLoading={isAwaitingPool}
                        selectedCreatorCount={selectedAccountIds.size}
                        onSelect={handlePoolSelect}
                    />

                    <span className={styles.countLabel}>
                        {isLoading || isAwaitingPool ? (
                            <Skeleton className={styles.countSkeleton} />
                        ) : typeof listedCount === 'number' ? (
                            <>
                                {`${listedCount} creator${listedCount !== 1 ? 's' : ''}`}
                                <HiddenCount
                                    hiddenCount={hiddenCount}
                                    isLoading={isHiddenCountLoading}
                                    platform={campaign?.platform}
                                />
                            </>
                        ) : null}
                    </span>

                    <Button
                        variant="default"
                        size="xs"
                        className={styles.addButton}
                        onClick={handleAddCreators}
                        isLoading={isAdding}
                        disabled={isAdding || !selectedAccountIds.size}
                    >
                        Add {selectedAccountIds.size || ''} creator
                        {selectedAccountIds.size === 1 ? '' : 's'}
                    </Button>
                </div>

                <section className={cn(styles.section, styles.tableSection)}>
                    <PoolCreatorsTable
                        data={listedCreators}
                        meta={meta}
                        onCreatorToggle={toggleCreator}
                        onSelectAll={selectCreators}
                        onClearSelection={clearSelection}
                        haveCreatorsBeenAdded={addedAccountIds.size > 0}
                        hasPoolsError={hasPoolsError}
                        hasNoPools={hasNoPools}
                        className={styles.tableContainer}
                    />
                </section>
            </Sheet.Content>
        </Sheet>
    );
};

type HiddenCountProps = {
    hiddenCount: number | undefined;
    isLoading: boolean;
    platform: creatorbase.Platform | undefined;
};

/** Resolves after the count beside it, so it carries its own skeleton rather than holding that one back. */
const HiddenCount = ({ hiddenCount, isLoading, platform }: HiddenCountProps) => {
    if (isLoading) {
        return <Skeleton className={styles.hiddenCountSkeleton} />;
    }

    if (!hiddenCount) {
        return null;
    }

    const subject = hiddenCount === 1 ? 'member has' : 'members have';
    const platformLabel = platform ? `${platformLabels[platform]} ` : '';

    return (
        <Tooltip delayDuration={100}>
            <Tooltip.Trigger asChild>
                <span className={styles.hiddenCount}>({hiddenCount} hidden)</span>
            </Tooltip.Trigger>
            <Tooltip.Content side="top">{`${hiddenCount} pool ${subject} no ${platformLabel}account`}</Tooltip.Content>
        </Tooltip>
    );
};

/** base-ui dispatches the press from a document listener, so the target is the only element on it. */
const isOverlayPress = (event: Event) => {
    const target = event.target;
    return target instanceof Element && Boolean(target.closest('[data-slot*="overlay"]'));
};

export default AddFromPoolModal;
